import {
  ActionIcon,
  Anchor,
  AppShell,
  Box,
  Burger,
  Container,
  Divider,
  Center,
  Group,
  Image,
  Loader,
  NavLink,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBook2, IconBrandGithub, IconMoon, IconSun } from "@tabler/icons-react";
import { Suspense, useEffect } from "react";
import { Link, NavLink as RouterNavLink, Outlet, useLocation } from "react-router";
import { CALCULATORS } from "../calculators";
import { LINKS } from "../config";
import { BrandMark } from "./BrandMark";

function ColorSchemeToggle() {
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme("light");
  const next = computed === "dark" ? "light" : "dark";
  return (
    <Tooltip label={`Switch to ${next} mode`}>
      <ActionIcon variant="subtle" color="gray" size="lg" aria-label={`Switch to ${next} mode`} onClick={() => setColorScheme(next)}>
        {computed === "dark" ? <IconSun size={20} /> : <IconMoon size={20} />}
      </ActionIcon>
    </Tooltip>
  );
}

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const location = useLocation();
  useEffect(close, [location.pathname, close]);

  return (
    <AppShell header={{ height: 64 }} navbar={{ width: 280, breakpoint: "md", collapsed: { desktop: true, mobile: !opened } }} padding={0}>
      <AppShell.Header className="app-header">
        <Container size="xl" h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Burger opened={opened} onClick={toggle} hiddenFrom="md" size="sm" aria-label="Toggle navigation" />
              <UnstyledButton component={Link} to="/" className="brand">
                <Group gap={10} wrap="nowrap">
                  <BrandMark size={34} />
                  <Box>
                    <Text fw={700} lh={1.1}>
                      BMP Hydrology Calculator 2.0
                    </Text>
                    <Text size="xs" c="dimmed" lh={1.1} visibleFrom="xs">
                      Southern California Coastal Water Research Project
                    </Text>
                  </Box>
                </Group>
              </UnstyledButton>
            </Group>
            <Group gap={4} visibleFrom="md" component="nav" aria-label="Calculators">
              {CALCULATORS.map((c) => (
                <RouterNavLink key={c.path} to={c.path} className="top-link">
                  <c.icon size={18} stroke={1.8} />
                  <span>{c.short}</span>
                </RouterNavLink>
              ))}
            </Group>
            <Group gap={4} wrap="nowrap">
              <Tooltip label="API documentation">
                <ActionIcon component={Link} to="/docs/api" variant="subtle" color="gray" size="lg" aria-label="API documentation">
                  <IconBook2 size={20} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Source code">
                <ActionIcon component="a" href={LINKS.sourceCode} target="_blank" rel="noreferrer" variant="subtle" color="gray" size="lg" aria-label="Source code">
                  <IconBrandGithub size={20} />
                </ActionIcon>
              </Tooltip>
              <ColorSchemeToggle />
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <Stack gap={4}>
          <NavLink component={RouterNavLink} to="/" label="Home" />
          {CALCULATORS.map((c) => (
            <NavLink key={c.path} component={RouterNavLink} to={c.path} label={c.name} leftSection={<c.icon size={18} />} />
          ))}
          <NavLink component={RouterNavLink} to="/docs/api" label="API documentation" leftSection={<IconBook2 size={18} />} />
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Box className="page-body">
          <Suspense
            fallback={
              <Center py={120}>
                <Loader />
              </Center>
            }
          >
            <Outlet />
          </Suspense>
        </Box>
        <Footer />
      </AppShell.Main>
    </AppShell>
  );
}

function Footer() {
  return (
    <Box component="footer" className="app-footer">
      <Container size="xl" py="xl">
        <Divider mb="lg" />
        <Group justify="space-between" align="flex-start" gap="lg">
          <Group gap="md" align="center">
            <Anchor href={LINKS.sccwrp} target="_blank" rel="noreferrer">
              <Image src="/sccwrp-logo.jpg" h={44} w="auto" alt="SCCWRP" className="footer-logo" />
            </Anchor>
            <Text size="sm" c="dimmed" maw={420}>
              Developed by the Southern California Coastal Water Research Project with support from the Southern California
              Stormwater Monitoring Coalition.
            </Text>
          </Group>
          <Stack gap={4}>
            <Text size="sm">
              Questions? <Anchor href={`mailto:${LINKS.contactEmail}`}>{LINKS.contactEmail}</Anchor>
            </Text>
            <Group gap="md">
              <Anchor size="sm" href={LINKS.feedbackRainfallFlow} target="_blank" rel="noreferrer">
                Feedback: rainfall &amp; flow
              </Anchor>
              <Anchor size="sm" href={LINKS.feedbackInfiltration} target="_blank" rel="noreferrer">
                Feedback: infiltration
              </Anchor>
            </Group>
          </Stack>
        </Group>
      </Container>
    </Box>
  );
}
